/**
 * Point Vote Service - Handles point-based voting on setlist sheets
 */

import { fetchWithAuth, API_URL } from './ApiService';

class PointVoteService {
  /**
   * Update user's point allocations for a setlist
   * @param {string} setlistId - The setlist ID
   * @param {Object} pointAllocations - Map of sheet IDs to point values
   * @param {string} comment - Optional comment about allocation strategy
   * @returns {Promise<Object>} Updated allocations and summaries
   */
  async updatePointAllocations(setlistId, pointAllocations, comment = '') {
    try {
      const response = await fetchWithAuth(`${API_URL}/point-votes/${setlistId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          point_allocations: pointAllocations,
          comment
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update point allocations');
      }

      return response;
    } catch (error) {
      console.error('Error updating point allocations:', error);
      throw error;
    }
  }

  /**
   * Get current user's point allocations for a setlist
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<Object>} User's current allocations
   */
  async getMyPointAllocations(setlistId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/point-votes/${setlistId}/my-allocations`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get point allocations');
      }

      return response;
    } catch (error) {
      console.error('Error getting point allocations:', error);
      throw error;
    }
  }

  /**
   * Get point summaries for all sheets in a setlist
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<Object>} Point summaries for all sheets
   */
  async getSetlistPointSummaries(setlistId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/point-votes/${setlistId}/summaries`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get point summaries');
      }

      return response;
    } catch (error) {
      console.error('Error getting point summaries:', error);
      throw error;
    }
  }

  /**
   * Get point summary for a specific sheet
   * @param {string} setlistId - The setlist ID
   * @param {string} sheetId - The sheet ID
   * @returns {Promise<Object>} Point summary for the sheet
   */
  async getSheetPointSummary(setlistId, sheetId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/point-votes/${setlistId}/${sheetId}/summary`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get sheet point summary');
      }

      return response.summary;
    } catch (error) {
      console.error('Error getting sheet point summary:', error);
      throw error;
    }
  }

  /**
   * Clear all point allocations for user in a setlist
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<Object>} Updated summaries
   */
  async clearPointAllocations(setlistId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/point-votes/${setlistId}`, {
        method: 'DELETE'
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to clear point allocations');
      }

      return response;
    } catch (error) {
      console.error('Error clearing point allocations:', error);
      throw error;
    }
  }

  /**
   * Validate point allocations before sending to server
   * @param {Object} allocations - Map of sheet IDs to point values
   * @param {number} maxPoints - Maximum allowed total points
   * @returns {Object} Validation result with isValid and errors
   */
  validatePointAllocations(allocations, maxPoints) {
    const errors = [];
    let totalPoints = 0;

    // Check each allocation
    for (const [sheetId, points] of Object.entries(allocations)) {
      if (points < 0) {
        errors.push(`Points for sheet ${sheetId} cannot be negative`);
      }
      if (!Number.isInteger(points)) {
        errors.push(`Points for sheet ${sheetId} must be a whole number`);
      }
      totalPoints += points;
    }

    // Check total constraint
    if (totalPoints > maxPoints) {
      errors.push(`Total points (${totalPoints}) cannot exceed maximum allowed (${maxPoints})`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      totalPoints
    };
  }

  /**
   * Calculate remaining points for a user
   * @param {Object} allocations - Current allocations
   * @param {number} maxPoints - Maximum allowed points
   * @returns {number} Remaining points
   */
  calculateRemainingPoints(allocations, maxPoints) {
    const totalUsed = Object.values(allocations).reduce((sum, points) => sum + points, 0);
    return Math.max(0, maxPoints - totalUsed);
  }

  /**
   * Get the sheet with the highest points
   * @param {Object} summaries - Point summaries object
   * @returns {string|null} Sheet ID with most points
   */
  getMostPopularSheet(summaries) {
    let maxPoints = 0;
    let mostPopularSheetId = null;

    for (const [sheetId, summary] of Object.entries(summaries)) {
      if (summary.total_points > maxPoints) {
        maxPoints = summary.total_points;
        mostPopularSheetId = sheetId;
      }
    }

    return mostPopularSheetId;
  }

  /**
   * Format point summary for display
   * @param {Object} summary - Raw point summary from API
   * @returns {Object} Formatted summary
   */
  formatPointSummary(summary) {
    return {
      totalPoints: summary.total_points || 0,
      voterCount: summary.voter_count || 0,
      voters: summary.voters || [],
      hasVotes: (summary.total_points || 0) > 0
    };
  }
}

// Export singleton instance
const pointVoteService = new PointVoteService();
export default pointVoteService;